import { Component } from '@angular/core';
import { environment } from '@y42/environment';
import { Y42FeatureType } from '@y42/json-schema';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, IRowNode } from 'ag-grid-community';
import { EMPTY_CHILD_NAME, EMPTY_CHILD_PATH } from '../types/empty-child';

export interface SubEntryRendererParams {
  previewButton: boolean;
  generateQueryButton: boolean;
  hideDetails: boolean;
  tablePreview: (params: ICellRendererParams) => void;
  generateQuery: (params: ICellRendererParams) => void | Promise<void>;
}

type SubEntryCellRendererParams = ICellRendererParams & SubEntryRendererParams;

@Component({
  selector: 'y42-sub-entry-renderer',
  template: `
    <p class="m-0" *ngIf="params.node.group">{{ childrenCount }}</p>
    <div *ngIf="!params.node.group" class="flex justify-between relative">
      <div *ngIf="params.previewButton" [matTooltip]="params.value" class="truncate">
        <button
          mat-button
          matTooltip="Preview Table"
          class="grid-table-preview-btn"
          type="button"
          (click)="tablePreview($event)"
        >
          <y42-feature-type-child-icon
            [featureType]="Y42FeatureType.Integration"
            [customClasses]="['top-0.5', 'mr-0']"
          ></y42-feature-type-child-icon>
        </button>
        {{ params.value }}
      </div>
      <div *ngIf="!params.previewButton" [matTooltip]="params.value" class="truncate">
        {{ params.value }}
      </div>
      <button
        *ngIf="params.generateQueryButton"
        mat-button
        matTooltip="Generate Query"
        class="ml-2"
        (click)="generateQuery($event)"
      >
        <fa-icon class="fs-08" [icon]="['fal', 'play']"></fa-icon>
      </button>
      <y42-open-in-asset-sidebar-button
        class="hover-button-open-in-sidebar"
        #openInAssetSidebarButton
        [class.active]="openInAssetSidebarButton.state.isActive"
        (click)="$event.stopPropagation()"
        *ngIf="!params.hideDetails && params.value !== emptyTableValue"
        [path]="params.data.child_$path"
      >
      </y42-open-in-asset-sidebar-button>
    </div>
  `,
})
export class SubEntryCellRendererComponent implements ICellRendererAngularComp {
  params!: SubEntryCellRendererParams;
  childrenCount = 0;

  readonly Y42FeatureType = Y42FeatureType;
  readonly emptyTableValue = EMPTY_CHILD_NAME;

  agInit(params: SubEntryCellRendererParams): void {
    this.params = params;
    this.childrenCount = this.getChildrenCount(params.node);
  }

  refresh(params: SubEntryCellRendererParams): boolean {
    this.params = params;
    this.childrenCount = this.getChildrenCount(params.node);
    return false;
  }

  tablePreview(evt: MouseEvent) {
    if (!environment.production_build && !this.params.tablePreview) {
      console.error('Table Preview button not implemented');
    }

    this.params.tablePreview?.(this.params);
    evt.stopPropagation();
    evt.preventDefault();
    return false;
  }

  generateQuery(evt: MouseEvent) {
    evt.stopPropagation();
    this.params.generateQuery?.(this.params);
  }

  private getChildrenCount(rowNode: IRowNode) {
    if (!rowNode.group || !rowNode.allLeafChildren) {
      return 0;
    }

    if (rowNode.allLeafChildren.length === 1) {
      const firstLeafChild = rowNode.allLeafChildren[0]?.data;
      if (firstLeafChild?.child_$path === EMPTY_CHILD_PATH) {
        return 0;
      } else {
        return 1;
      }
    } else {
      return rowNode.allLeafChildren.length;
    }
  }
}
